import { focusHaloFlush, MorphPanel, panelMotion, usePanelDismiss } from "@pax/ui";
import { Check, ChevronDown } from "lucide-react";
import { type ReactNode, useCallback, useState } from "react";

/** A lab's, router's or host's mark, on a white tile so every brand reads on either theme. */
export function Mark({ src, size = "size-4" }: { src?: string; size?: string }) {
  if (!src) return null;
  return (
    <span
      aria-hidden
      className={`${size} grid shrink-0 place-items-center overflow-hidden rounded-[5px] bg-white ring-1 ring-foreground/10`}
    >
      <img src={src} alt="" className="size-[78%] object-contain" />
    </span>
  );
}

const dropdownSurface =
  "rounded-2xl bg-background shadow-[0_4px_12px_rgb(0_0_0/0.06),0_24px_48px_-16px_rgb(0_0_0/0.2)] ring-1 ring-foreground/10";

export const optionRow = `flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-sm hover:bg-foreground/[0.07] ${panelMotion} ${focusHaloFlush}`;

export const panelHeading = "px-3 pt-2 pb-1 text-xs text-muted-foreground";

export const capitalized = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * The trigger becomes the panel. `view` names what the open panel is showing, so it can change
 * shape from one step to the next.
 */
export function Popover({
  face,
  faceClassName,
  view = "open",
  onClosed,
  children,
}: {
  face: ReactNode;
  faceClassName: string;
  view?: string;
  onClosed?: () => void;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => {
    setOpen(false);
    onClosed?.();
  }, [onClosed]);
  const rootRef = usePanelDismiss<HTMLSpanElement>({ open, onDismiss: close });

  return (
    <span ref={rootRef} className="relative inline-block align-middle">
      <span aria-hidden className={`invisible ${faceClassName}`}>
        {face}
      </span>
      <span className={`absolute top-0 left-0 ${open ? "z-30" : "z-20"}`}>
        <MorphPanel
          view={open ? view : "face"}
          anchor="top-left"
          className={open ? dropdownSurface : `rounded-2xl hover:bg-foreground/5 ${panelMotion}`}
        >
          {open ? (
            children(close)
          ) : (
            <button type="button" onClick={() => setOpen(true)} className={faceClassName}>
              {face}
            </button>
          )}
        </MorphPanel>
      </span>
    </span>
  );
}

export interface Option {
  id: string;
  label: string;
  selected?: boolean;
}

const pickerFace =
  "flex h-8 items-center gap-1.5 px-3 text-sm font-medium whitespace-nowrap text-foreground";

/** One word of a sentence that reads as the question, and a list of what it could say instead. */
export function Picker({
  label,
  groups,
  onPick,
}: {
  label: string;
  groups: Option[][];
  onPick: (id: string) => void;
}) {
  return (
    <Popover
      faceClassName={pickerFace}
      face={
        <>
          {label}
          <ChevronDown className="size-3.5 text-muted-foreground" />
        </>
      }
    >
      {close => (
        <div className="max-h-96 w-60 overflow-y-auto p-1">
          {groups.map((options, index) => (
            <div
              key={options[0]?.id ?? index}
              className={index > 0 ? "mt-1 border-t border-foreground/[0.06] pt-1" : ""}
            >
              {options.map(option => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => {
                    onPick(option.id);
                    close();
                  }}
                  className={optionRow}
                >
                  <span className={`flex-1 truncate ${option.selected ? "font-medium" : ""}`}>
                    {option.label}
                  </span>
                  {option.selected && <Check className="size-3.5 shrink-0" />}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </Popover>
  );
}

export function Section({
  label,
  action,
  children,
}: {
  label: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="mt-9">
      <div className="mb-1 flex h-8 items-center justify-between gap-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        {action}
      </div>
      {children}
    </section>
  );
}

/** A box for choices that combine, a dot for choices that replace each other. */
export function Tick({ chosen, many }: { chosen: boolean; many: boolean }) {
  return (
    <span
      className={`grid size-4 shrink-0 place-items-center ring-1 ${many ? "rounded-[5px]" : "rounded-full"} ${
        chosen ? "bg-foreground text-background ring-foreground" : "ring-foreground/20"
      }`}
    >
      {chosen &&
        (many ? (
          <Check className="size-3" />
        ) : (
          <span className="size-1.5 rounded-full bg-background" />
        ))}
    </span>
  );
}

export function Facts({ facts }: { facts: [string, ReactNode][] }) {
  return (
    <dl className="mt-6 grid grid-cols-3 gap-x-6 gap-y-3">
      {facts.map(([name, value]) => (
        <div key={name}>
          <dt className="text-xs text-muted-foreground">{name}</dt>
          <dd className="text-sm tabular-nums">{value}</dd>
        </div>
      ))}
    </dl>
  );
}
